import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/site/PageHero';
import { Search, ArrowUpRight, AlertTriangle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { listPosts, wpImage, wpDate, wpReadTime, wpExcerpt, wpCategories } from '../lib/wp';

function Skeleton() {
  return (
    <div className="animate-pulse">
      <div className="aspect-[4/3] bg-[#0f1a14]/5" />
      <div className="mt-5 space-y-3">
        <div className="h-3 w-32 bg-[#0f1a14]/10" />
        <div className="h-6 w-4/5 bg-[#0f1a14]/15" />
        <div className="h-3 w-full bg-[#0f1a14]/10" />
        <div className="h-3 w-3/4 bg-[#0f1a14]/10" />
      </div>
    </div>
  );
}

export default function Blog() {
  const [q, setQ] = useState('');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const timer = setTimeout(async () => {
      try {
        const data = await listPosts({ perPage: 30, search: q });
        if (!cancelled) setPosts(data);
      } catch (err) {
        if (!cancelled) setError('Could not load articles right now. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, q ? 400 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [q]);

  return (
    <div>
      <PageHero
        eyebrow="Journal"
        title="Stories, guides & mountain notes."
        subtitle="Insights on real estate, hill‑country living and everything in between."
        image="/gallery_2025/Lumii_20230624_101408965.jpg"
      />
      <section className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex items-center gap-3 max-w-md border border-[#0f1a14]/15 bg-[#f6f1e7] px-4 mb-12">
            <Search className="h-4 w-4 text-[#8a6b3a]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search articles..."
              className="border-0 bg-transparent focus-visible:ring-0 shadow-none"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-600 border border-red-200 bg-red-50 p-4 mb-8">
              <AlertTriangle className="h-4 w-4" /> {error}
            </div>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {loading && Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} />)}

            {!loading && posts.map((p) => {
              const img = wpImage(p);
              const cats = wpCategories(p);
              return (
                <Link key={p.id} to={`/blog/${p.slug}`} className="group block">
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#f6f1e7]">
                    {img ? (
                      <img
                        src={img}
                        alt={p.title?.rendered || ''}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    ) : (
                      <div className="h-full w-full grid place-items-center text-[#8a6b3a]/40 font-serif text-4xl">E</div>
                    )}
                    {cats[0] && (
                      <span className="absolute top-4 left-4 bg-[#c9a56a] text-[#0f1a14] px-3 py-1 text-[10px] uppercase tracking-widest">
                        {cats[0]}
                      </span>
                    )}
                  </div>
                  <div className="mt-5">
                    <div className="text-xs uppercase tracking-widest text-[#8a6b3a]">
                      {wpDate(p)} · {wpReadTime(p)} read
                    </div>
                    <h3
                      className="font-serif text-2xl text-[#0f1a14] mt-3 leading-snug group-hover:text-[#8a6b3a] transition-colors"
                      dangerouslySetInnerHTML={{ __html: p.title?.rendered || '' }}
                    />
                    <p className="text-[#0f1a14]/60 mt-3 text-sm leading-relaxed line-clamp-3">{wpExcerpt(p)}</p>
                    <div className="mt-4 inline-flex items-center gap-1 text-[#8a6b3a] text-xs uppercase tracking-widest">
                      Read article <ArrowUpRight className="h-3 w-3" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {!loading && !error && posts.length === 0 && (
            <div className="text-center text-[#0f1a14]/50 py-16">No articles found for “{q}”.</div>
          )}
        </div>
      </section>
    </div>
  );
}
